import { ExpressionSpecification } from 'mapbox-gl';
import { ComponentProps, useMemo, useState } from 'react';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import MapLocation from '@alltrails/amplitude/enums/MapLocation';
import TrailCardLocation from '@alltrails/amplitude/enums/TrailCardLocation';
import { useExperiments } from '@alltrails/experiments';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { TrailActivityType } from '@alltrails/trails';
import useActiveTrailCoordinates from '../../hooks/useActiveTrailCoordinates';
import useShiftKey from '../../hooks/useShiftKey';
import useTrailPinInteractions from '../../hooks/useTrailPinInteractions';
import useTrailPinSvgDefinitions from '../../hooks/useTrailPinSvgDefinitions';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateActiveTrails } from '../../redux/reducer';
import { buildCommonLayoutProps } from '../../utils/buildPinIconExpressions';
import { serializedCoordinatesToCoordinates } from '../../utils/serializedCoordinates';
import { pillPopupOffset, pinPopupOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import useSvgImages from '../../utils/useSvgImages';
import ActiveTrailPopup from './ActiveTrailPopup';
import CollapsedTrailPinLayer from './CollapsedTrailPinLayer';
import RegularTrailPinLayer from './RegularTrailPinLayer';

export type TrailPinsProps = {
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  cardLocation?: TrailCardLocation;
  detailedCardLocation?: CardLocation;
  mapLocation?: MapLocation | string; // Accept string for legacy PageStrings support
  sourceId: string;
  /**
   * When using a symbol layer with both icon AND text, all of the text renders on top of all of the icons in that layer.
   * So, if we try to apply the trail count as text for each multi-trail pin, we run into a bug where the counts render on top of other pins.
   * To fix this, we need to include the text in the actual image that we use for the icon, which requires us to generate these images dynamically.
   * trailCounts is the set of counts that we need for each pin in order to generate these images. closedTrailCounts is the same thing, but for closed trailheads.
   */
  trailCounts: Set<number>;
  closedTrailCounts: Set<number>;
  savedTrailCounts: Set<number>;
  completedTrailCounts: Set<number>;
} & Pick<ComponentProps<typeof ActiveTrailPopup>, 'onTrailCardViewed' | 'onTrailNavigate'>;

const TrailPins = ({
  activityType,
  cardLocation,
  detailedCardLocation,
  mapLocation,
  onTrailCardViewed,
  onTrailNavigate,
  sourceId,
  trailCounts,
  closedTrailCounts,
  savedTrailCounts,
  completedTrailCounts
}: TrailPinsProps) => {
  const dispatch = useAppDispatch();
  const experiments = useExperiments();
  const hasNewPinsExperiment = experiments?.['web-discover-new-pin-explore-rm']?.value === 'on';
  const viewedPinSerializedCoordinatesThisSession = useAppSelector(state => state.map.viewedPinSerializedCoordinatesThisSession);

  const analyticsInfo = useMemo(
    () =>
      detailedCardLocation && mapLocation
        ? {
            detailedCardLocation,
            page: mapLocation
          }
        : undefined,
    [detailedCardLocation, mapLocation]
  );
  const { isComplete, isFavorite, isVerified, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(undefined, undefined, analyticsInfo);
  const { activeTrails, trailResults } = useAppSelector(state => ({
    activeTrails: state.map.activeTrails,
    trailResults: state.map.trailResults
  }));

  const [hoveredSerializedCoordinates, setHoveredSerializedCoordinates] = useState('');
  const [selectedSerializedCoordinates, setSelectedSerializedCoordinates] = useState('');

  const { onMouseEnter, onMouseLeave } = useTrailPinInteractions(
    isFavorite,
    isComplete,
    selectedSerializedCoordinates,
    setHoveredSerializedCoordinates,
    setSelectedSerializedCoordinates,
    cardLocation,
    mapLocation
  );
  const { activeTrailSerializedCoordinates } = useActiveTrailCoordinates(selectedSerializedCoordinates, setSelectedSerializedCoordinates);
  const { isShiftPressed } = useShiftKey();
  const isHoveredExpression: ExpressionSpecification = ['==', ['get', 'serializedCoordinates'], hoveredSerializedCoordinates || ''];
  const isSelectedExpression: ExpressionSpecification = ['==', ['get', 'serializedCoordinates'], selectedSerializedCoordinates || ''];
  const isViewedExpression: ExpressionSpecification = [
    'in',
    ['get', 'serializedCoordinates'],
    ['literal', viewedPinSerializedCoordinatesThisSession]
  ];
  const isActiveTrailExpression: ExpressionSpecification = ['==', ['get', 'serializedCoordinates'], activeTrailSerializedCoordinates || ''];
  const commonLayoutProps = buildCommonLayoutProps(isHoveredExpression, isSelectedExpression);

  const popup = useMemo(() => {
    if (!trailResults || activeTrails?.length !== 1 || !activeTrailSerializedCoordinates) {
      return null;
    }
    if (isShiftPressed) {
      return null;
    }

    // When the active trail is part of a selected multi trailhead, we show the carousel popup - otherwise, just show the trail card
    const trailsAtActiveTrailhead = trailResults[activeTrailSerializedCoordinates];
    const isSelectedMultiTrail = selectedSerializedCoordinates === activeTrailSerializedCoordinates && trailsAtActiveTrailhead?.length > 1;
    const initialTrailIndex = isSelectedMultiTrail ? trailsAtActiveTrailhead.findIndex(({ ID }) => ID === activeTrails[0].ID) : undefined;
    const trailsInPopup = isSelectedMultiTrail ? trailsAtActiveTrailhead : activeTrails;

    const { lat, lng } = serializedCoordinatesToCoordinates(activeTrailSerializedCoordinates);
    return (
      <ActiveTrailPopup
        activityType={activityType}
        cardLocation={cardLocation}
        detailedCardLocation={detailedCardLocation}
        initialTrailIndex={initialTrailIndex}
        isComplete={id => isComplete('trail', id)}
        isFavorite={id => isFavorite('trail', id)}
        isVerified={id => isVerified('trail', id)}
        latitude={lat}
        longitude={lng}
        offset={hasNewPinsExperiment ? pillPopupOffset : pinPopupOffset}
        onFavoriteClick={id => handleFavoriteClick('trail', id)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTrailCardViewed={onTrailCardViewed}
        onTrailNavigate={onTrailNavigate}
        onTrailChange={trail => {
          dispatch(updateActiveTrails(trail ? [trail] : undefined));
        }}
        trails={trailsInPopup}
      />
    );
  }, [
    activeTrailSerializedCoordinates,
    activeTrails,
    activityType,
    cardLocation,
    detailedCardLocation,
    dispatch,
    handleFavoriteClick,
    isShiftPressed,
    isComplete,
    isFavorite,
    isVerified,
    onMouseEnter,
    onMouseLeave,
    onTrailCardViewed,
    onTrailNavigate,
    hasNewPinsExperiment,
    selectedSerializedCoordinates,
    trailResults
  ]);

  const svgDefinitions = useTrailPinSvgDefinitions({ trailCounts, closedTrailCounts, savedTrailCounts, completedTrailCounts, hasNewPinsExperiment });
  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  if (!imagesAreLoaded) {
    return null;
  }

  return (
    <>
      <CollapsedTrailPinLayer
        sourceId={sourceId}
        isHoveredExpression={isHoveredExpression}
        isSelectedExpression={isSelectedExpression}
        isViewedExpression={isViewedExpression}
        isActiveTrailExpression={isActiveTrailExpression}
        commonLayoutProps={commonLayoutProps}
      />
      <RegularTrailPinLayer
        sourceId={sourceId}
        isHoveredExpression={isHoveredExpression}
        isSelectedExpression={isSelectedExpression}
        isViewedExpression={isViewedExpression}
        isActiveTrailExpression={isActiveTrailExpression}
        commonLayoutProps={commonLayoutProps}
      />
      {popup}
      {listModal}
      {signUpModal}
    </>
  );
};

export default TrailPins;
